import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import React, {
  CSSProperties,
  RefObject,
} from 'react';
import {
  Document,
  Page,
} from 'react-pdf';
import clsx from 'clsx';
import head from 'lodash/head';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import {
  AddressDTO,
} from 'dtos';
import ToolTip from 'core/components/Tooltip';
import {
  getAddressString,
} from 'utils/string';
import {
  FillableFormField,
  FillableFormSession,
  FormFieldsData,
} from 'views/EMR/components/FillableForm/types';
import {
  getFieldHorizontalAlignment,
  getFieldTextAlignment,
  getFieldVerticalAlignment,
  getStableKey,
  getStyleFromRectForPage,
} from 'views/EMR/components/FillableForm/utils/fieldPositioning';
import {
  handlePdfLoadError,
  handlePdfSourceError,
} from 'views/EMR/components/FillableForm/utils/pdfDiagnostics';
import {
  formatPhone,
  notEmptyString,
} from '@populateui/utils';

function formatWithAffix(
  value: string | number,
  options?: { prefix?: string; suffix?: string },
): string {
  const str = String(value ?? '');
  const { prefix = '', suffix = '' } = options ?? {};
  return str.trim() === '' ? '' : `${prefix}${str}${suffix}`;
}

const formatterByType: Record<string, (val: string) => string> = {
  phone: (val) => {
    if (val?.replace(/\D/g, '').length <= 1) {
      return '';
    }
    return formatPhone(val);
  },
  dollar: (val) => formatWithAffix(val, { prefix: '$' }),
  percent: (val) => formatWithAffix(val, { suffix: '%' }),
};

type Props = {
  session: FillableFormSession;
  containerWidth: number;
  formValues: FormFieldsData;
  currentFieldId?: string;
  invalidFieldIds?: string[];
  isFormFillable: boolean;
  fieldsRefs: RefObject<Record<string, HTMLElement>>;
  onFieldActivate?: (fieldId: string) => void;
  onFieldValueChange?: (fieldId: string, value: boolean | string | string[]) => void;
  onLoadSuccess: () => void;
  onError: (message: string) => void;
};

type OverlayElement = 'button' | 'div';

function getRawFieldValue(
  formValues: FormFieldsData,
  fieldId?: string | null,
) {
  return get(formValues, [fieldId ?? '', 'value']);
}

function getSelectedChildIds(
  formValues: FormFieldsData,
  fieldId?: string | null,
): string[] {
  const rawValue = getRawFieldValue(formValues, fieldId);
  return isArray(rawValue)
    ? rawValue.filter((childId) => notEmptyString(childId))
    : [];
}

function getFieldDisplayValue(
  field: FillableFormField,
  formValues: FormFieldsData,
): string {
  const rawValue = getRawFieldValue(formValues, field.id);

  if (field.type === 'usAddress') {
    return getAddressString(rawValue as AddressDTO, false);
  }

  if (field.type === 'checkbox') {
    return rawValue === true ? '✔' : '';
  }

  const formatterType = field.type === 'phone' || /phone/i.test(field.aliasId ?? '')
    ? 'phone'
    : field.type;
  const format = formatterByType[formatterType] ?? ((val: string) => val);
  return format((rawValue as string) ?? '');
}

function fieldBelongsToPage(field: FillableFormField, pageNumber: number) {
  return Number(field.pageNum ?? 0) === pageNumber;
}

function getOverlayStyle(
  field: FillableFormField,
  pageHeight: number,
  scaleFactor: number,
  options?: { isSignField?: boolean; fontSize?: number | null },
): CSSProperties {
  const isSignField = options?.isSignField ?? false;

  return {
    ...field?.rect
      ? getStyleFromRectForPage(field, pageHeight, scaleFactor, isSignField)
      : {},
    justifyContent: getFieldHorizontalAlignment(field.horizontalAlignment),
    alignItems: getFieldVerticalAlignment(field.verticalAlignment),
    textAlign: getFieldTextAlignment(field.horizontalAlignment),
    fontSize: options?.fontSize ? `${options.fontSize}px` : undefined,
    zIndex: 100,
  };
}

function setFieldRef(
  fieldsRefs: RefObject<Record<string, HTMLElement>>,
  fieldId?: string | null,
) {
  return (element: HTMLElement | null) => {
    if (element && fieldId) {
      fieldsRefs.current[fieldId] = element;
    }
  };
}

function getOverlayClassName(
  options: {
    isCurrentField: boolean;
    hasValidationError?: boolean;
    isClickable?: boolean;
    isCentered?: boolean;
    isRadioOption?: boolean;
  },
) {
  return clsx(
    'pdf-overlay-text flex text-[8px] sm:text-sm lg:text-base',
    {
      'appearance-none cursor-pointer': options.isClickable,
      'whitespace-pre-wrap': !options.isRadioOption,
      'items-center': options.isRadioOption,
      'text-center': options.isCentered,
      '!border-2 !border-blue-500': options.isCurrentField,
      '!border-red-500 !bg-red-50': options.hasValidationError,
    },
  );
}

function getOverlayElement(isClickable: boolean): OverlayElement {
  return isClickable ? 'button' : 'div';
}

export default function PdfFormViewer({
  session,
  containerWidth,
  formValues,
  currentFieldId,
  invalidFieldIds = [],
  isFormFillable,
  fieldsRefs,
  onFieldActivate,
  onFieldValueChange,
  onLoadSuccess,
  onError,
}: Props) {
  const invalidFieldIdSet = new Set(invalidFieldIds);
  const pageWidth = head(session.pages)?.width ?? 0;
  const pageHeight = head(session.pages)?.height ?? 0;
  const scaleFactor = pageWidth > 0 ? containerWidth / pageWidth : 1;
  const renderingHeight = pageHeight * scaleFactor;
  const renderedPageWidth = pageWidth * (renderingHeight / pageHeight);
  const fileName = head(session.formPdf?.list)?.fileName;

  return (
    <Document
      file={session.pdfUrl ?? undefined}
      onLoadSuccess={onLoadSuccess}
      onSourceError={(error) => {
        onError(handlePdfSourceError(error, session));
      }}
      onLoadError={async (error) => {
        const message = await handlePdfLoadError(error, session);
        onError(message);
      }}
    >
      {session.pages?.map((page) => {
        const pageNumber = page?.pageNumber ?? 0;
        const currentPageNumber = pageNumber + 1;
        const pageKey = `page-${pageNumber}-${page?.width ?? ''}x${page?.height ?? ''}`;
        const pageFields = session.fields.filter(
          (field) => fieldBelongsToPage(field, pageNumber),
        );

        return (
          <div key={pageKey} className="mb-2">
            <ToolTip title={fileName ?? ''}>
              <div className="truncate font-medium text-md px-2">
                {fileName}
              </div>
            </ToolTip>
            <div
              className="relative overflow-hidden"
              style={{ width: renderedPageWidth, height: renderingHeight }}
            >
              <Page
                renderTextLayer={false}
                renderAnnotationLayer={false}
                pageNumber={currentPageNumber}
                height={renderingHeight}
                width={renderedPageWidth}
                className="pointer-events-none"
              />
              {pageFields.map((field) => {
                const isCurrentField = field.id === currentFieldId && isFormFillable;
                const hasValidationError = Boolean(field.id) && invalidFieldIdSet.has(field.id ?? '');
                const value = getFieldDisplayValue(field, formValues);
                const isIconText = ['radioGroup', 'checkbox'].includes(field?.type);
                const isSignField = ['signature', 'initial'].includes(field?.type);
                const isSignatureImage = isSignField
                  && typeof value === 'string'
                  && value.startsWith('data:image');
                const fieldKey = getStableKey(field);
                const rawCheckboxValue = getRawFieldValue(formValues, field.id);
                const selectedCheckboxGroupChildIds = field.type === 'checkboxGroup'
                  ? getSelectedChildIds(formValues, field.id)
                  : [];
                const isCheckboxFieldClickable = isFormFillable
                  && field.type === 'checkbox'
                  && Boolean(field.id);
                const FieldOverlay = getOverlayElement(isCheckboxFieldClickable);

                if (Number(field.pageNum) < 0) {
                  return null;
                }

                return (
                  <div key={fieldKey}>
                    {field.type !== 'radioGroup' && field.type !== 'checkboxGroup' && (
                      <FieldOverlay
                        {...(isCheckboxFieldClickable ? { type: 'button' as const } : {})}
                        ref={setFieldRef(fieldsRefs, field.id)}
                        style={getOverlayStyle(field, pageHeight, scaleFactor, {
                          isSignField,
                          fontSize: field.fontSize,
                        })}
                        className={getOverlayClassName({
                          isCurrentField,
                          hasValidationError,
                          isClickable: isCheckboxFieldClickable,
                          isCentered: isIconText,
                        })}
                        data-invalid={hasValidationError || undefined}
                        aria-invalid={hasValidationError}
                        onClick={() => {
                          if (!isCheckboxFieldClickable) {
                            return;
                          }

                          onFieldActivate?.(field.id ?? '');
                          onFieldValueChange?.(field.id ?? '', rawCheckboxValue !== true);
                        }}
                      >
                        {!isIconText && (
                          isSignatureImage
                            ? (
                              <img
                                src={value}
                                alt="signature"
                                className="h-full object-contain text-[#345D7E] dancing-script-regular"
                              />
                            )
                            : (
                              <span className={clsx({ 'dancing-script-regular text-[#345D7E]': isSignField })}>
                                {value}
                              </span>
                            )
                        )}
                        {field?.type === 'checkbox' ? (
                          <span className="pdf-overlay-check-mark text-xs">{value}</span>
                        ) : null}
                      </FieldOverlay>
                    )}
                    {field.type === 'radioGroup' && (field?.children?.length ?? 0) > 0 && field?.children?.map((child) => {
                      const isRadioFieldClickable = isFormFillable && Boolean(field.id) && Boolean(child?.id);
                      const RadioOverlay = getOverlayElement(isRadioFieldClickable);

                      return (
                        <RadioOverlay
                          key={getStableKey(child)}
                          {...(isRadioFieldClickable ? { type: 'button' as const } : {})}
                          ref={setFieldRef(fieldsRefs, field.id)}
                          style={getOverlayStyle(child, pageHeight, scaleFactor)}
                          className={getOverlayClassName({
                            isCurrentField,
                            hasValidationError,
                            isClickable: isRadioFieldClickable,
                            isRadioOption: true,
                          })}
                          data-invalid={hasValidationError || undefined}
                          aria-invalid={hasValidationError}
                          onClick={() => {
                            if (!isRadioFieldClickable) {
                              return;
                            }

                            onFieldActivate?.(field.id ?? '');
                            onFieldValueChange?.(field.id ?? '', child.id ?? '');
                          }}
                        >
                          {value === child?.id ? (
                            <span className="pdf-overlay-check-mark text-xs">✔</span>
                          ) : null}
                        </RadioOverlay>
                      );
                    })}
                    {field.type === 'checkboxGroup' && (field?.children?.length ?? 0) > 0 && field?.children?.map((child) => {
                      const isCheckboxGroupOptionClickable = isFormFillable
                        && notEmptyString(field.id ?? '')
                        && notEmptyString(child?.id ?? '');
                      const CheckboxGroupOverlay = getOverlayElement(isCheckboxGroupOptionClickable);
                      const childId = child?.id ?? '';
                      const isChildSelected = selectedCheckboxGroupChildIds.includes(childId);

                      return (
                        <CheckboxGroupOverlay
                          key={getStableKey(child)}
                          {...(isCheckboxGroupOptionClickable ? { type: 'button' as const } : {})}
                          ref={setFieldRef(fieldsRefs, field.id)}
                          style={getOverlayStyle(child, pageHeight, scaleFactor)}
                          className={getOverlayClassName({
                            isCurrentField,
                            hasValidationError,
                            isClickable: isCheckboxGroupOptionClickable,
                            isRadioOption: true,
                          })}
                          data-invalid={hasValidationError || undefined}
                          aria-invalid={hasValidationError}
                          onClick={() => {
                            if (!isCheckboxGroupOptionClickable) {
                              return;
                            }

                            const nextSelectedChildIds = isChildSelected
                              ? selectedCheckboxGroupChildIds.filter(
                                (selectedChildId) => selectedChildId !== childId,
                              )
                              : [...selectedCheckboxGroupChildIds, childId];
                            onFieldActivate?.(field.id ?? '');
                            onFieldValueChange?.(field.id ?? '', nextSelectedChildIds);
                          }}
                        >
                          {isChildSelected ? (
                            <span className="pdf-overlay-check-mark text-xs">✔</span>
                          ) : null}
                        </CheckboxGroupOverlay>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end text-xs text-gray-600 py-1 px-2">
              <div>
                Page
                {' '}
                {currentPageNumber}
                {' '}
                of
                {' '}
                {session.pages?.length}
              </div>
            </div>
          </div>
        );
      })}
    </Document>
  );
}
